import React from 'react';
import { MapPin, ArrowRight } from 'lucide-react';

const ExpansionMapSection = () => {
  const expansionPlan = [
    {
      phase: "Phase 1 - Now",
      location: "Tampa, Florida",
      status: "Launching",
      description: "Our founding city where it all begins. First 1,000 members get exclusive access.",
      color: "from-green-500 to-green-600",
      textColor: "text-green-600",
      bgColor: "bg-green-100"
    },
    {
      phase: "Phase 2 - Q2 2026", 
      location: "Central Florida",
      status: "Expansion",
      description: "Orlando, Lakeland, and surrounding Central Florida communities.",
      color: "from-blue-500 to-blue-600",
      textColor: "text-blue-600",
      bgColor: "bg-blue-100"
    },
    {
      phase: "Phase 3 - Q3 2026",
      location: "Arizona",
      status: "Regional Launch",
      description: "Phoenix, Scottsdale, and greater Arizona metropolitan areas.",
      color: "from-orange-500 to-orange-600", 
      textColor: "text-orange-600",
      bgColor: "bg-orange-100"
    },
    {
      phase: "Phase 4 - Q4 2026",
      location: "Texas",
      status: "Multi-City Launch",
      description: "Dallas, Houston, Austin, and San Antonio - completing our initial expansion.",
      color: "from-purple-500 to-purple-600",
      textColor: "text-purple-600",
      bgColor: "bg-purple-100"
    }
  ];

  return (
    <section id="expansion-section" className="py-24 bg-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-primary rounded-full opacity-20"></div>
        <div className="absolute top-3/4 right-1/3 w-3 h-3 bg-orange-500 rounded-full opacity-20"></div>
        <div className="absolute bottom-1/4 left-1/2 w-1 h-1 bg-primary rounded-full opacity-30"></div>
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary rounded-full px-6 py-3 text-sm font-semibold mb-8">
            <MapPin className="w-4 h-4" />
            Strategic Expansion Plan
          </div>
          
          <h2 className="text-4xl md:text-6xl font-black text-gray-900 mb-8 leading-tight">
            Starting in <span className="text-transparent bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text">Tampa</span>,<br />
            Expanding Nationwide
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            ProConnect is strategically launching in high-growth markets across the Sun Belt. 
            As a founding member, you'll have priority access in all expansion cities.
          </p>
        </div>

        {/* Expansion Timeline */}
        <div className="space-y-8 mb-16">
          {expansionPlan?.map((phase, index) => (
            <div key={index} className="flex flex-col md:flex-row items-start md:items-center gap-8">
              {/* Phase Indicator */}
              <div className={`flex-shrink-0 w-64 ${phase?.bgColor} rounded-2xl p-6 text-center`}>
                <div className={`text-sm font-semibold ${phase?.textColor} mb-2`}>
                  {phase?.phase}
                </div>
                <div className="text-lg font-bold text-gray-900 mb-1">
                  {phase?.location}
                </div>
                <div className={`text-sm font-medium ${phase?.textColor}`}>
                  {phase?.status}
                </div>
              </div>

              {/* Arrow Connector */}
              {index < expansionPlan?.length - 1 && (
                <div className="hidden md:block">
                  <ArrowRight className="w-8 h-8 text-gray-400" />
                </div>
              )}

              {/* Description */}
              <div className="flex-grow">
                <p className="text-lg text-gray-600 leading-relaxed">
                  {phase?.description}
                </p>
              </div>

              {/* Visual Icon */}
              <div className={`flex-shrink-0 w-16 h-16 bg-gradient-to-br ${phase?.color} rounded-2xl flex items-center justify-center`}>
                <MapPin className="w-8 h-8 text-white" />
              </div>
            </div>
          ))}
        </div>

        {/* Founding Member Geographic Benefits */}
        <div className="bg-gradient-to-r from-gray-50 to-white rounded-3xl p-8 md:p-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Founding Members Get Multi-City Access
              </h3>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-gray-700">
                    <strong>Priority Access:</strong> First to use ProConnect when we launch in new cities
                  </p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-gray-700">
                    <strong>Travel Benefits:</strong> Use your founding member benefits in all ProConnect cities
                  </p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-gray-700">
                    <strong>Expansion Updates:</strong> Get early notifications about new city launches
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center">
              <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100">
                <div className="text-5xl font-black text-primary mb-4">4</div>
                <div className="text-xl font-semibold text-gray-900 mb-2">Launch Cities</div>
                <div className="text-gray-600 mb-6">Planned for 2026</div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 px-4 bg-gray-50 rounded-xl">
                    <span className="text-gray-700 font-medium">Tampa, FL</span>
                    <span className="text-green-600 text-sm font-semibold">Active</span>
                  </div>
                  <div className="flex items-center justify-between py-2 px-4 bg-gray-50 rounded-xl">
                    <span className="text-gray-700 font-medium">Central FL</span>
                    <span className="text-blue-600 text-sm font-semibold">Q2 2026</span>
                  </div>
                  <div className="flex items-center justify-between py-2 px-4 bg-gray-50 rounded-xl">
                    <span className="text-gray-700 font-medium">Arizona</span>
                    <span className="text-orange-600 text-sm font-semibold">Q3 2026</span>
                  </div>
                  <div className="flex items-center justify-between py-2 px-4 bg-gray-50 rounded-xl">
                    <span className="text-gray-700 font-medium">Texas</span>
                    <span className="text-purple-600 text-sm font-semibold">Q4 2026</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExpansionMapSection;